import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'elevated' | 'outlined' | 'glass' | 'gradient';
  size?: 'sm' | 'md' | 'lg';
  hover?: boolean;
  animated?: boolean;
  onClick?: () => void;
}

export const Card: React.FC<CardProps> = ({
  children,
  className = '',
  variant = 'default',
  size = 'md',
  hover = true,
  animated = true,
  onClick,
}) => {
  const baseClasses = "rounded-xl transition-all duration-300 ease-out";

  const variantClasses = {
    default: "bg-white shadow-soft border border-neutral-200/50",
    elevated: "bg-white shadow-hard border border-neutral-200/30",
    outlined: "bg-white border-2 border-neutral-200 shadow-xs",
    glass: "bg-white/80 backdrop-blur-sm shadow-medium border border-white/20",
    gradient: "bg-gradient-to-br from-white to-neutral-50/80 shadow-medium border border-neutral-200/40",
  };

  const sizeClasses = {
    sm: "p-4",
    md: "p-6",
    lg: "p-8",
  };

  const hoverClasses = hover
    ? "hover:shadow-lg hover:-translate-y-1 hover:scale-[1.02] cursor-pointer"
    : "";

  const combinedClasses = cn(
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    hoverClasses,
    className
  );

  if (animated) {
    return (
      <motion.div
        className={combinedClasses}
        onClick={onClick}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        whileHover={hover ? { y: -2, scale: 1.02 } : {}}
        whileTap={onClick ? { scale: 0.98 } : {}}
      >
        {children}
      </motion.div>
    );
  }

  return (
    <div
      className={combinedClasses}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

export const CardHeader: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => (
  <div className={cn("border-b border-neutral-200/50 pb-4 mb-4", className)}>
    {children}
  </div>
);

export const CardTitle: React.FC<{
  children: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}> = ({ children, className = '', size = 'md' }) => {
  const sizeClasses = {
    sm: "text-lg font-semibold",
    md: "text-xl font-semibold",
    lg: "text-2xl font-bold",
  };

  return (
    <h3 className={cn(sizeClasses[size], "text-neutral-900 mb-2", className)}>
      {children}
    </h3>
  );
};

export const CardDescription: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => (
  <p className={cn("text-neutral-600 text-sm", className)}>
    {children}
  </p>
);

export const CardContent: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => (
  <div className={cn("", className)}>
    {children}
  </div>
);

export const CardFooter: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => (
  <div className={cn("border-t border-neutral-200/50 pt-4 mt-4", className)}>
    {children}
  </div>
);

export default Card;